import { Disclosure } from '@headlessui/react'
import { ChevronRightIcon } from '@heroicons/react/24/outline'
import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ISidebarNavigationItem } from '../model/interfaces/ISidebarNavigationItem'

function classNames(...classes: any[]) {
  return classes.filter(Boolean).join(' ')
}

// Helper function to render label badge
const renderLabelBadge = (label?: string) => {
  if (!label) return null
  return (
    <span className="ml-2 inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
      {label}
    </span>
  )
}

interface Props {
  navigation: ISidebarNavigationItem[]
}

export const SidebarNavigation: React.FC<Props> = ({ navigation }) => {
  const location = useLocation()
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({})

  useEffect(() => {
    const storedOpenItems: Record<string, boolean> = {}
    navigation.forEach((item) => {
      if (item.children) {
        storedOpenItems[item.name] = localStorage.getItem(`sidebar-${item.name}`) === 'true'
        // Only first level items are collapsible, so we only need to store their state
      }
    })
    setOpenItems(storedOpenItems)
  }, [navigation])

  const toggleDisclosure = (itemName: string) => {
    setOpenItems((prev) => {
      const newOpenState = !prev[itemName]
      localStorage.setItem(`sidebar-${itemName}`, newOpenState.toString())

      return { ...prev, [itemName]: newOpenState }
    })
  }

  return (
    <div className="mt-3 h-full grow flex-col gap-y-5 overflow-y-autobg-white pr-5">
      <nav className="flex flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul role="list" className="-mx-2 space-y-1">
              {navigation.map((item) => {
                if (item.hidden) return null

                const current = item.href === location.pathname
                const isOpen = openItems[item.name] ?? false

                return (
                  <li key={item.name}>
                    {!item.children ? (
                      <Link
                        to={item.href}
                        className={classNames(
                          current ? 'bg-blue-600 text-white' : 'hover:bg-blue-600 hover:text-white',
                          'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold text-gray-700 group',
                        )}
                      >
                        <item.icon
                          className={classNames(
                            current ? 'text-white' : 'group-hover:text-white',
                            'h-6 w-6 shrink-0 text-gray-400',
                          )}
                          aria-hidden="true"
                        />
                        <div className="flex items-center">
                          {item.name}
                          {renderLabelBadge(item.label)}
                        </div>
                      </Link>
                    ) : (
                      <Disclosure key={`${item.name}-${isOpen}`} as="div" defaultOpen={isOpen}>
                        {({ open }) => (
                          <>
                            <Disclosure.Button
                              onClick={() => {
                                toggleDisclosure(item.name)
                              }}
                              className={classNames(
                                current
                                  ? 'bg-blue-600 text-white'
                                  : 'hover:bg-blue-600 hover:text-white',
                                'group flex items-center w-full text-left rounded-md p-2 gap-x-3 text-sm leading-6 font-semibold text-gray-700',
                              )}
                            >
                              <item.icon
                                className={classNames(
                                  current ? 'text-white' : 'group-hover:text-white',
                                  'h-6 w-6 shrink-0 text-gray-400',
                                )}
                                aria-hidden="true"
                              />
                              <div className="flex items-center">
                                {item.name}
                                {renderLabelBadge(item.label)}
                              </div>
                              <ChevronRightIcon
                                className={classNames(
                                  open ? 'rotate-90 text-gray-500' : 'text-gray-400',
                                  'ml-auto h-5 w-5 shrink-0',
                                )}
                                aria-hidden="true"
                              />
                            </Disclosure.Button>
                            <Disclosure.Panel as="ul" className="mt-1 px-2">
                              {item.children?.map((subItem) => {
                                if (subItem.hidden) {
                                  return null
                                }

                                const current = subItem.href === location.pathname
                                const hasChildren = subItem.children && subItem.children.length > 0

                                return (
                                  <li key={subItem.name}>
                                    {!hasChildren ? (
                                      <Link
                                        to={subItem.href}
                                        className={classNames(
                                          current
                                            ? 'bg-blue-600 text-white'
                                            : 'hover:bg-blue-600 hover:text-white',
                                          'rounded-md py-2 pr-2 pl-9 text-sm leading-6 text-gray-700 flex gap-x-3',
                                        )}
                                      >
                                        <div className="flex items-center">
                                          {subItem.name}
                                          {renderLabelBadge(subItem.label)}
                                        </div>
                                      </Link>
                                    ) : (
                                      <div>
                                        <div
                                          className={classNames(
                                            current
                                              ? 'bg-blue-600 text-white'
                                              : 'hover:bg-blue-600 hover:text-white',
                                            'group flex items-center w-full text-left rounded-md py-2 pr-2 pl-9 gap-x-3 text-sm leading-6 font-semibold text-gray-700',
                                          )}
                                        >
                                          <Link to={subItem.href} className="flex-grow">
                                            <div className="flex items-center">
                                              {subItem.name}
                                              {renderLabelBadge(subItem.label)}
                                            </div>
                                          </Link>
                                        </div>
                                        <ul className="mt-1 px-2">
                                          {subItem.children?.map((grandChildItem) => {
                                            if (grandChildItem.hidden) {
                                              return null
                                            }

                                            const current =
                                              grandChildItem.href === location.pathname
                                            return (
                                              <li key={grandChildItem.name}>
                                                <Link
                                                  to={grandChildItem.href}
                                                  className={classNames(
                                                    current
                                                      ? 'bg-blue-600 text-white'
                                                      : 'hover:bg-blue-600 hover:text-white',
                                                    'rounded-md py-2 pr-2 pl-12 text-sm leading-6 text-gray-700 flex gap-x-3',
                                                  )}
                                                >
                                                  <div className="flex items-center">
                                                    {grandChildItem.name}
                                                    {renderLabelBadge(grandChildItem.label)}
                                                  </div>
                                                </Link>
                                              </li>
                                            )
                                          })}
                                        </ul>
                                      </div>
                                    )}
                                  </li>
                                )
                              })}
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                    )}
                  </li>
                )
              })}
            </ul>
          </li>
        </ul>
      </nav>
    </div>
  )
}
